/**
 * Form submission utility for ImpactQuill website
 * Handles form data submission to the API and provides consistent error handling
 */

// API Configuration
const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'http://127.0.0.1:8000';

export interface FormSubmissionData {
  [key: string]: string | number | boolean;
}

export interface FormSubmissionOptions {
  formName: string;
  formSource?: string;
  onSuccess?: (response: any) => void;
  onError?: (error: any) => void;
  onLoading?: (loading: boolean) => void;
}

export interface FormSubmissionResponse {
  success: boolean;
  message: string;
  data?: {
    submission_id: string;
    timestamp: string;
    fields_processed?: number;
  };
  errors?: any;
}

/**
 * Submit form data to the API using GET method
 * @param formData - The form data as key-value pairs
 * @param options - Configuration options for the submission
 * @returns Promise<FormSubmissionResponse>
 */
export async function submitForm(
  formData: FormSubmissionData,
  options: FormSubmissionOptions
): Promise<FormSubmissionResponse> {
  const { formName, formSource = 'website', onLoading, onSuccess, onError } = options;

  try {
    // Show loading state
    if (onLoading) {
      onLoading(true);
    }

    // Create query parameters for GET request
    const params = new URLSearchParams({
      form_name: formName,
      form_source: formSource,
      form_data: JSON.stringify(formData)
    });

    // Make the API request using GET method
    const response = await fetch(`${API_BASE_URL}/api/forms/submit?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    const result: FormSubmissionResponse = await response.json();

    if (response.ok && result.success) {
      // Success callback
      if (onSuccess) {
        onSuccess(result);
      }
      return result;
    } else {
      // Error callback
      if (onError) {
        onError(result);
      }
      return result;
    }
  } catch (error) {
    const errorResponse: FormSubmissionResponse = {
      success: false,
      message: 'Network error or server unavailable. Please try again.',
      errors: error,
    };

    // Error callback
    if (onError) {
      onError(errorResponse);
    }

    return errorResponse;
  } finally {
    // Hide loading state
    if (onLoading) {
      onLoading(false);
    }
  }
}

/**
 * Validate form data before submission
 * @param formData - The form data to validate
 * @param requiredFields - Array of required field names
 * @returns { isValid: boolean, errors: string[] }
 */
export function validateFormData(
  formData: FormSubmissionData,
  requiredFields: string[]
): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Check required fields
  for (const field of requiredFields) {
    if (!formData[field] || String(formData[field]).trim() === '') {
      errors.push(`${field.replace(/_/g, ' ')} is required`);
    }
  }

  // Validate email fields
  for (const [key, value] of Object.entries(formData)) {
    if (key.toLowerCase().includes('email') && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(String(value))) {
        errors.push('Please enter a valid email address');
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Create a form submission handler with common functionality
 * @param options - Configuration options
 * @returns Function that can be used as an event handler
 */
export function createFormHandler(options: FormSubmissionOptions) {
  return async (event: Event) => {
    event.preventDefault();
    
    const form = event.target as HTMLFormElement;
    const formData = new FormData(form);
    
    // Convert FormData to plain object
    const data: FormSubmissionData = {};
    for (const [key, value] of formData.entries()) {
      data[key] = value as string;
    }

    // Validate form data
    const requiredFields = Array.from(form.querySelectorAll('[required]')).map(
      (el) => (el as HTMLInputElement).name
    );
    
    const validation = validateFormData(data, requiredFields);
    if (!validation.isValid) {
      if (options.onError) {
        options.onError({
          success: false,
          message: 'Please fix the following errors:',
          errors: validation.errors,
        });
      }
      return;
    }

    // Submit the form
    await submitForm(data, options);
  };
}

/**
 * Show a success message to the user
 * @param message - The message to display
 * @param container - The container element to show the message in
 */
export function showSuccessMessage(message: string, container?: HTMLElement) {
  const successDiv = document.createElement('div');
  successDiv.className = 'bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4';
  successDiv.innerHTML = `
    <div class="flex items-center">
      <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
      </svg>
      <span>${message}</span>
    </div>
  `;

  if (container) {
    container.insertBefore(successDiv, container.firstChild);
  } else {
    document.body.appendChild(successDiv);
  }

  // Auto-remove after 5 seconds
  setTimeout(() => {
    successDiv.remove();
  }, 5000);
}

/**
 * Show an error message to the user
 * @param message - The message to display
 * @param errors - Additional error details
 * @param container - The container element to show the message in
 */
export function showErrorMessage(
  message: string,
  errors?: any,
  container?: HTMLElement
) {
  const errorDiv = document.createElement('div');
  errorDiv.className = 'bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4';
  
  let errorContent = `
    <div class="flex items-center">
      <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
      </svg>
      <span>${message}</span>
    </div>
  `;

  if (errors && Array.isArray(errors)) {
    errorContent += `
      <ul class="mt-2 list-disc list-inside">
        ${errors.map((error: string) => `<li>${error}</li>`).join('')}
      </ul>
    `;
  }

  errorDiv.innerHTML = errorContent;

  if (container) {
    container.insertBefore(errorDiv, container.firstChild);
  } else {
    document.body.appendChild(errorDiv);
  }

  // Auto-remove after 8 seconds
  setTimeout(() => {
    errorDiv.remove();
  }, 8000);
} 